import { Box, Divider, Stack, TextField, Typography } from '@mui/material';
import { type RestoreAccount, Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { BulletItem } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/bulletItem';
import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { TerraformBashToggle } from '@/components/accounts/terraformBashToggle';
import { StepErrorDialog } from '@/components/dialogs/stepErrorDialog';
import { CustomAccountDisplayName } from '@/components/form/customAccountDisplayName';
import { FormTextField } from '@/components/form/formTextField';
import { getCloudFieldConfig } from '@/components/form/validation/cloudFieldValidation';
import { SelectTargetAccount } from '@/components/restore/steps/selectTargetAccount';
import { CopyableCode } from '@/components/shared/copyableCode';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { FEATURES_SUPPORT_VERSIONS } from '@/configs/featureConfig';
import { useDAL } from '@/contexts/useDAL';
import { useUser } from '@/contexts/useUser';

import { getMongoAtlasRestoreProjectCode } from './mongoAtlasRestoreCode';

export interface MongoAtlasConnectRestoreProjectState {
  connectMethod: 'terraform' | 'bash';
  projectId: string;
  apiPublicKey: string;
  apiPrivateKey: string;
  restoreAccount?: RestoreAccount;
}

const projectIdConfig = getCloudFieldConfig(Provider.MongoAtlas, 'projectId');

export const ConnectMongoAtlasRestoreProject = (
  props: StepProps<MongoAtlasConnectRestoreProjectState>
) => {
  const [isDeploying, setIsDeploying] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [customName, setCustomName] = useState('');
  const user = useUser();
  const dal = useDAL();

  const restoreAccountId =
    props.wizardState.restoreAccount?.providerAccountId || '';

  const code = getMongoAtlasRestoreProjectCode(
    props.wizardState.projectId,
    user.eonAccount?.id ?? '',
    restoreAccountId,
    props.wizardState.connectMethod
  );

  const isMissingCodeFields = !props.wizardState.projectId || !restoreAccountId;

  const canGoNext = () => {
    const validProjectId = projectIdConfig.validation.validateInput(
      props.wizardState.projectId
    );
    const hasApiKeys =
      !!props.wizardState.apiPublicKey && !!props.wizardState.apiPrivateKey;
    const hasRestoreAccount = !!props.wizardState.restoreAccount;

    return !isDeploying && validProjectId && hasApiKeys && hasRestoreAccount;
  };

  const onNextClick = async () => {
    setError(null);
    setIsDeploying(true);

    try {
      await dal.cloudAccounts.restore.connect.mongoAtlas({
        name: customName || undefined,
        apiPublicKey: props.wizardState.apiPublicKey,
        apiPrivateKey: props.wizardState.apiPrivateKey,
        atlasProjectId: props.wizardState.projectId,
        awsRestoreAccountId: props.wizardState.restoreAccount!.id,
      });
      await props.finish();
    } catch (error: unknown) {
      const errorMessage = await extractErrorMessage(error);
      setError(new Error(errorMessage));
    } finally {
      setIsDeploying(false);
    }
  };

  const bulletTitle = `Provide your project ID, ${
    props.wizardState.connectMethod === 'terraform'
      ? 'apply the generated Terraform module'
      : 'run the generated Bash command'
  } in your environment, then enter the Eon API key from the output below.`;

  return (
    <>
      <StepContainer
        stepperIndex={0}
        hideStepper
        stepperLabels={props.stepperLabels}
        canGoNext={canGoNext}
        nextButtonDisabledTooltip={
          isDeploying
            ? 'Please wait for the deployment to complete'
            : 'Please fill all required fields'
        }
        nextButtonText='Connect'
        backButtonText='Cancel'
        onBackClick={props.back}
        sx={{ overflow: 'auto', paddingX: '0px', paddingTop: '24px' }}
        onNextClick={onNextClick}
      >
        <Stack divider={<Divider />} gap='24px'>
          <TerraformBashToggle
            text='Run the setup below using your own Atlas API key with Project Owner permissions on the target project. It will create a dedicated Eon API key with minimal permissions.'
            connectMethod={props.wizardState.connectMethod}
            setConnectMethod={(connectMethod) => {
              props.setWizardState({ ...props.wizardState, connectMethod });
            }}
          />
          <Stack gap='24px' divider={<Divider />}>
            <Box padding='16px min(100px,6vw) 36px' width='100%' height='auto'>
              <BulletItem
                title={bulletTitle}
                leftContent={
                  <Stack
                    gap='24px'
                    divider={<Divider sx={{ borderStyle: 'dashed' }} />}
                  >
                    <Stack gap='24px'>
                      <Box sx={{ my: -2 }}>
                        <SelectTargetAccount
                          supportedCloudProviders={[Provider.Aws]}
                          restoreAccount={props.wizardState.restoreAccount}
                          supportedInstalledVersion={
                            FEATURES_SUPPORT_VERSIONS.MONGO_ATLAS_ONBOARDING
                          }
                          setRestoreAccountState={props.setWizardState}
                          title='Select an AWS restore account for MongoDB Atlas operations:'
                          emptyMessage='You must first add an AWS restore account before connecting an Atlas restore project. Go to Settings > Restore Accounts > AWS accounts to add one.'
                        />
                      </Box>
                      <Typography variant='body2' color='text.secondary'>
                        MongoDB Atlas restore operations will run within the
                        selected AWS restore account. Atlas API keys will be
                        securely stored in its Secrets Manager.
                      </Typography>
                      <FormTextField
                        title='Project ID'
                        placeholder={projectIdConfig.placeholder}
                        validation={projectIdConfig.validation}
                        value={props.wizardState.projectId || ''}
                        onChange={(event) =>
                          props.setWizardState({
                            ...props.wizardState,
                            projectId: event.target.value,
                          })
                        }
                      />
                      <Typography variant='body2' color='text.secondary'>
                        After running the setup, enter the Eon API key from the
                        output below.
                      </Typography>
                      <TextField
                        label='Eon API Public Key'
                        value={props.wizardState.apiPublicKey}
                        onChange={(e) =>
                          props.setWizardState({
                            ...props.wizardState,
                            apiPublicKey: e.target.value,
                          })
                        }
                        disabled={isDeploying}
                        fullWidth
                        size='small'
                      />
                      <TextField
                        label='Eon API Private Key'
                        type='password'
                        value={props.wizardState.apiPrivateKey}
                        onChange={(e) =>
                          props.setWizardState({
                            ...props.wizardState,
                            apiPrivateKey: e.target.value,
                          })
                        }
                        disabled={isDeploying}
                        fullWidth
                        size='small'
                      />
                    </Stack>
                    <CustomAccountDisplayName
                      entityName='project'
                      customName={customName}
                      setCustomName={setCustomName}
                    />
                  </Stack>
                }
                rightContent={
                  <CopyableCode
                    title={
                      props.wizardState.connectMethod === 'terraform'
                        ? 'main.tf'
                        : 'mongo-atlas-eon-setup.sh'
                    }
                    code={code}
                    blur={isMissingCodeFields}
                  />
                }
              />
            </Box>
          </Stack>
        </Stack>
      </StepContainer>
      <StepErrorDialog
        isOpen={!!error}
        onClose={() => {
          setError(null);
          setIsDeploying(false);
        }}
        error={error}
      />
    </>
  );
};
